import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  DollarSign, 
  Upload, 
  CheckCircle, 
  Clock, 
  XCircle,
  Copy,
  ExternalLink,
  Camera,
  AlertCircle,
  ArrowRight,
  Loader2,
  ChevronLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AddCredits = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1); // 1: select amount, 2: payment, 3: submit proof, 4: status
  const [loading, setLoading] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [deposit, setDeposit] = useState(null);
  const [transactionId, setTransactionId] = useState('');
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pendingDeposit, setPendingDeposit] = useState(null);
  const [depositConfig, setDepositConfig] = useState({ min_deposit: 5, max_deposit: 500, credit_conversion_rate: 100 });
  const [paymentMethod, setPaymentMethod] = useState('cashapp'); // cashapp or venmo
  const [paymentMethods, setPaymentMethods] = useState({
    cashapp: { name: 'Cash App', username: '$MatchStakeApp', link: 'https://cash.app/$MatchStakeApp', qr_code: '/cashapp-qr.jpg' },
    venmo: { name: 'Venmo', username: '@matchstakeapp', link: 'https://www.paypal.com/qrcodes/venmocs/f4fda714-45d3-432b-bb0f-f1ce757da3e6', qr_code: '/venmo-qr.jpg' }
  });

  useEffect(() => {
    fetchTiers();
    checkPendingDeposit();
  }, []);

  const fetchTiers = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/api/wallet/deposit/tiers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTiers(res.data.tiers);
      setDepositConfig({
        min_deposit: res.data.min_deposit || 5,
        max_deposit: res.data.max_deposit || 500,
        credit_conversion_rate: res.data.credit_conversion_rate || 100
      });
      // Load payment methods from API
      if (res.data.payment_methods) {
        setPaymentMethods(res.data.payment_methods);
      }
    } catch (err) {
      toast.error('Failed to load deposit options');
    }
  };

  const checkPendingDeposit = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/api/wallet/deposit/manual/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pending = res.data.deposits.find(d => d.status === 'pending' || d.status === 'under_review');
      if (pending) {
        setPendingDeposit(pending);
        setDeposit(pending);
        if (pending.status === 'pending') {
          setStep(3);
        } else {
          setStep(4);
        }
      }
    } catch (err) {
      console.error('Failed to check pending deposits');
    }
  };

  // Get amount to use (preset tier or custom)
  const getDepositAmount = () => {
    if (selectedTier) return selectedTier.amount_usd;
    if (customAmount && parseFloat(customAmount) >= depositConfig.min_deposit) return parseFloat(customAmount);
    return 0;
  };

  const getCreditsAmount = () => {
    const amount = getDepositAmount();
    if (selectedTier) return selectedTier.credits;
    return Math.floor(amount * depositConfig.credit_conversion_rate);
  };

  const createDeposit = async () => {
    const amount = getDepositAmount();
    if (!amount || amount < depositConfig.min_deposit) {
      toast.error(`Minimum deposit is $${depositConfig.min_deposit}`);
      return;
    }
    if (amount > depositConfig.max_deposit) {
      toast.error(`Maximum deposit is $${depositConfig.max_deposit}`);
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.post(`${API}/api/wallet/deposit/manual/create`, {
        amount: amount,
        payment_method: paymentMethod
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeposit(res.data);
      setStep(2);
      const methodName = paymentMethods[paymentMethod]?.name || paymentMethod;
      toast.success(`Deposit created! Complete payment on ${methodName}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create deposit');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setScreenshotFile(file);
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('session_token');
      const res = await axios.post(`${API}/api/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setScreenshotFile({ name: file.name, url: res.data.url });
      toast.success('Screenshot uploaded!');
    } catch (err) {
      toast.error('Failed to upload screenshot');
      setScreenshotFile(null);
    } finally {
      setUploading(false);
    }
  };

  const submitProof = async () => {
    if (!screenshotFile?.url && !transactionId) {
      toast.error('Please provide a screenshot or transaction ID');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/api/wallet/deposit/manual/submit`, {
        deposit_id: deposit.deposit_id,
        screenshot_url: screenshotFile?.url || null,
        transaction_id: transactionId || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Deposit submitted for verification!');
      setStep(4);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit proof');
    } finally {
      setLoading(false);
    }
  };

  const cancelDeposit = async () => {
    if (!deposit) return;
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/api/wallet/deposit/manual/${deposit.deposit_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deposit cancelled');
      setDeposit(null);
      setPendingDeposit(null);
      setStep(1);
    } catch (err) {
      toast.error('Failed to cancel deposit');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const StatusBadge = ({ status }) => {
    const config = {
      pending: { color: '#FFB800', bg: 'bg-[#FFB800]/10', icon: Clock, text: 'Pending Payment' },
      under_review: { color: '#00F0FF', bg: 'bg-[#00F0FF]/10', icon: Loader2, text: 'Under Review' },
      approved: { color: '#00FF94', bg: 'bg-[#00FF94]/10', icon: CheckCircle, text: 'Approved' },
      rejected: { color: '#FF2E2E', bg: 'bg-[#FF2E2E]/10', icon: XCircle, text: 'Rejected' }
    };
    const c = config[status] || config.pending;
    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${c.bg}`}>
        <c.icon className="w-4 h-4" style={{ color: c.color }} />
        <span style={{ color: c.color }} className="font-medium text-sm">{c.text}</span>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto" data-testid="add-credits-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate('/wallet')} className="p-2">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold">Deposit Money</h1>
            <p className="text-white/50 text-sm">Deposit money to your MatchStake balance to enter matches</p>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                ${step >= s ? 'bg-[#00F0FF] text-black' : 'bg-white/10 text-white/50'}`}>
                {s}
              </div>
              {s < 4 && <div className={`w-8 h-0.5 ${step > s ? 'bg-[#00F0FF]' : 'bg-white/10'}`} />}
            </div>
          ))}
        </div>

        <motion.div
          key={step}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-2xl p-6"
        >
          {/* Step 1: Select Amount */}
          {step === 1 && (
            <div data-testid="step-select-amount">
              <h2 className="font-heading text-xl font-semibold mb-6 text-center">Select Deposit Amount</h2>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                {tiers.map((tier) => (
                  <button
                    key={tier.amount_usd}
                    onClick={() => { setSelectedTier(tier); setCustomAmount(''); }}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      selectedTier?.amount_usd === tier.amount_usd && !customAmount
                        ? 'border-[#00F0FF] bg-[#00F0FF]/10'
                        : 'border-white/10 bg-white/5 hover:border-white/30'
                    }`}
                    data-testid={`tier-${tier.amount_usd}`}
                  >
                    <p className="font-mono text-2xl font-bold text-[#CCFF00]">
                      ${tier.amount_usd}
                    </p>
                    <p className="text-white/50 text-sm">deposit</p>
                  </button>
                ))}
              </div>

              {/* Custom Amount Input */}
              <div className="mb-6">
                <div className="flex items-center gap-4 mb-3">
                  <div className="flex-1 h-px bg-white/10" />
                  <span className="text-white/40 text-sm">Or enter custom amount</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>
                
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 font-mono text-lg">$</span>
                  <Input
                    type="number"
                    placeholder="Enter amount"
                    value={customAmount}
                    onChange={(e) => { 
                      setCustomAmount(e.target.value); 
                      setSelectedTier(null); 
                    }}
                    className="pl-10 input-dark text-lg font-mono"
                    min={depositConfig.min_deposit}
                    max={depositConfig.max_deposit}
                    data-testid="custom-amount-input"
                  />
                </div>
                
                {customAmount && parseFloat(customAmount) >= depositConfig.min_deposit && (
                  <div className="mt-3 p-3 rounded-lg bg-[#CCFF00]/10 border border-[#CCFF00]/30 flex justify-between items-center">
                    <span className="text-white/70">Deposit amount:</span>
                    <span className="font-mono font-bold text-[#CCFF00] text-xl">
                      ${parseFloat(customAmount).toFixed(2)}
                    </span>
                  </div>
                )}
                
                <p className="text-xs text-white/40 mt-2">
                  Minimum deposit: $5 • Maximum: $500
                </p>
              </div>

              <Button
                onClick={createDeposit}
                disabled={(!selectedTier && !customAmount) || loading || getDepositAmount() < depositConfig.min_deposit}
                className="w-full btn-primary py-6"
                data-testid="continue-btn"
              >
                {loading ? 'Creating...' : `Continue to Payment${getDepositAmount() > 0 ? ` ($${getDepositAmount()})` : ''}`}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          )}

          {/* Step 2: Payment Instructions */}
          {step === 2 && deposit && (
            <div data-testid="step-payment">
              <h2 className="font-heading text-xl font-semibold mb-2 text-center">Complete Payment</h2>
              <p className="text-white/50 text-sm text-center mb-6">
                Choose your preferred payment method
              </p>

              {/* Payment Amount & Code */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                  <span className="text-white/50">Amount to Send</span>
                  <span className="font-mono font-bold text-[#CCFF00] text-xl">${deposit.amount_usd?.toFixed(2)}</span>
                </div>
                
                <div className="flex items-center justify-between p-4 rounded-xl bg-[#00F0FF]/10 border border-[#00F0FF]/30">
                  <span className="text-white/70">Your Deposit Code</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-[#00F0FF] text-xl">{deposit.reference_code}</span>
                    <button onClick={() => copyToClipboard(deposit.reference_code)} className="text-[#00F0FF] hover:text-white">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Dual Payment Cards */}
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                {/* Cash App Card */}
                <div className="glass-card rounded-xl p-5 border-2 border-[#00D632]/30 hover:border-[#00D632]/60 transition-all">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-[#00D632] flex items-center justify-center">
                      <span className="text-white font-bold text-xl">$</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">Pay with Cash App</h3>
                      <p className="text-sm text-[#00D632]">Send to $MatchStakeApp</p>
                    </div>
                  </div>
                  
                  {/* Cash App QR Code */}
                  <div className="flex justify-center mb-4">
                    <div className="bg-white p-3 rounded-xl">
                      <img 
                        src="/cashapp-qr.jpg" 
                        alt="Cash App QR Code"
                        className="w-36 h-36 object-contain"
                      />
                    </div>
                  </div>

                  {/* Cash App Username */}
                  <div className="flex items-center justify-center gap-2 p-2 rounded-lg bg-[#00D632]/10 mb-4">
                    <span className="font-mono font-bold text-[#00D632]">$MatchStakeApp</span>
                    <button 
                      onClick={() => copyToClipboard('$MatchStakeApp')} 
                      className="text-[#00D632] hover:text-white"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>

                  <a 
                    href="https://cash.app/$MatchStakeApp" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block w-full"
                  >
                    <Button className="w-full bg-[#00D632] hover:bg-[#00B82A] text-black font-bold py-3">
                      Open Cash App
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                </div>

                {/* Venmo Card */}
                <div className="glass-card rounded-xl p-5 border-2 border-[#3D95CE]/30 hover:border-[#3D95CE]/60 transition-all">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-[#3D95CE] flex items-center justify-center">
                      <span className="text-white font-bold text-lg">V</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">Pay with Venmo</h3>
                      <p className="text-sm text-[#3D95CE]">Send to @matchstakeapp</p>
                    </div>
                  </div>
                  
                  {/* Venmo QR Code */}
                  <div className="flex justify-center mb-4">
                    <div className="bg-white p-3 rounded-xl">
                      <img 
                        src="/venmo-qr.jpg" 
                        alt="Venmo QR Code"
                        className="w-36 h-36 object-contain"
                      />
                    </div>
                  </div>

                  {/* Venmo Username */}
                  <div className="flex items-center justify-center gap-2 p-2 rounded-lg bg-[#3D95CE]/10 mb-4">
                    <span className="font-mono font-bold text-[#3D95CE]">@matchstakeapp</span>
                    <button 
                      onClick={() => copyToClipboard('@matchstakeapp')} 
                      className="text-[#3D95CE] hover:text-white"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>

                  <a 
                    href="https://www.paypal.com/qrcodes/venmocs/f4fda714-45d3-432b-bb0f-f1ce757da3e6" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block w-full"
                  >
                    <Button className="w-full bg-[#3D95CE] hover:bg-[#2A7AB0] text-white font-bold py-3">
                      Open Venmo
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                </div>
              </div>

              {/* Instructions */}
              <div className="p-4 rounded-xl bg-[#FFB800]/10 border border-[#FFB800]/30 mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-[#FFB800] flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-semibold text-[#FFB800] mb-2">Important Instructions</p>
                    <p className="text-white/70 mb-2">
                      Include your deposit code <span className="font-mono text-[#00F0FF] font-bold">{deposit.reference_code}</span> in the payment note to receive credits.
                    </p>
                    <p className="text-white/50 text-xs">
                      Users are responsible for entering the correct payment details and username. MatchStake is not responsible for incorrect submissions.
                    </p>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => setStep(3)}
                className="w-full btn-primary py-6"
                data-testid="payment-sent-btn"
              >
                I've Sent the Payment
              </Button>
            </div>
          )}

          {/* Step 3: Submit Proof */}
          {step === 3 && deposit && (
            <div data-testid="step-submit-proof">
              <h2 className="font-heading text-xl font-semibold mb-2 text-center">Verify Payment</h2>
              <p className="text-white/50 text-sm text-center mb-6">
                Upload proof of your {paymentMethods[deposit.payment_method || paymentMethod]?.name || 'payment'}
              </p>

              <div className="space-y-4 mb-6">
                {/* Screenshot Upload */}
                <div>
                  <label className="block text-sm text-white/50 mb-2">Payment Screenshot</label>
                  <div className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors
                    ${screenshotFile ? 'border-[#00FF94]/50 bg-[#00FF94]/5' : 'border-white/20 hover:border-white/40'}`}>
                    {uploading ? (
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin text-[#00F0FF]" />
                        <span>Uploading...</span>
                      </div>
                    ) : screenshotFile ? (
                      <div className="flex items-center justify-center gap-2 text-[#00FF94]">
                        <CheckCircle className="w-5 h-5" />
                        <span>{screenshotFile.name}</span>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <Camera className="w-8 h-8 mx-auto mb-2 text-white/40" />
                        <p className="text-white/60">Click to upload screenshot</p>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileUpload}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex-1 h-px bg-white/10" />
                  <span className="text-white/30 text-sm">OR</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>

                {/* Transaction ID */}
                <div>
                  <label className="block text-sm text-white/50 mb-2">Cash App Transaction ID</label>
                  <Input
                    value={transactionId}
                    onChange={(e) => setTransactionId(e.target.value)}
                    placeholder="Enter transaction ID from Cash App"
                    className="input-dark"
                  />
                </div>
              </div>

              {/* Deposit Info */}
              <div className="p-4 rounded-xl bg-white/5 mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-white/50">Deposit Code</span>
                  <span className="font-mono text-[#00F0FF]">{deposit.reference_code}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Amount</span>
                  <span className="font-mono">${deposit.amount_usd?.toFixed(2)} ({deposit.credits_amount?.toLocaleString()} credits)</span>
                </div>
              </div>

              <Button
                onClick={submitProof}
                disabled={loading || (!screenshotFile && !transactionId)}
                className="w-full btn-primary py-6"
                data-testid="submit-proof-btn"
              >
                {loading ? 'Submitting...' : 'Submit for Verification'}
              </Button>

              <Button
                onClick={cancelDeposit}
                variant="ghost"
                className="w-full mt-3 text-white/50 hover:text-[#FF2E2E]"
              >
                Cancel Deposit
              </Button>
            </div>
          )}

          {/* Step 4: Status */}
          {step === 4 && deposit && (
            <div data-testid="step-status" className="text-center">
              <StatusBadge status={deposit.status} />
              
              <h2 className="font-heading text-xl font-semibold mt-6 mb-2">
                {deposit.status === 'under_review' ? 'Verification in Progress' : 
                 deposit.status === 'approved' ? 'Deposit Approved!' :
                 deposit.status === 'rejected' ? 'Deposit Rejected' : 'Awaiting Payment'}
              </h2>
              
              <p className="text-white/50 text-sm mb-6">
                {deposit.status === 'under_review' 
                  ? 'Your deposit is being verified. This usually takes 1-5 minutes.'
                  : deposit.status === 'approved'
                  ? `${deposit.credits_amount?.toLocaleString()} credits have been added to your wallet!`
                  : deposit.status === 'rejected'
                  ? deposit.admin_note || 'Please contact support for assistance.'
                  : 'Complete your payment and submit proof.'}
              </p>

              {/* Deposit Details */}
              <div className="p-4 rounded-xl bg-white/5 text-left mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-white/50">Reference Code</span>
                  <span className="font-mono text-[#00F0FF]">{deposit.reference_code}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-white/50">Amount</span>
                  <span className="font-mono">${deposit.amount_usd?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Credits</span>
                  <span className="font-mono text-[#CCFF00]">{deposit.credits_amount?.toLocaleString()}</span>
                </div>
              </div>

              {deposit.status === 'approved' ? (
                <Button onClick={() => navigate('/wallet')} className="w-full btn-primary">
                  Go to Wallet
                </Button>
              ) : deposit.status === 'rejected' ? (
                <Button onClick={() => { setDeposit(null); setStep(1); }} className="w-full btn-primary">
                  Try Again
                </Button>
              ) : (
                <Button 
                  onClick={async () => {
                    const token = localStorage.getItem('session_token');
                    const res = await axios.get(`${API}/api/wallet/deposit/manual/status/${deposit.deposit_id}`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    setDeposit(res.data);
                  }} 
                  variant="outline" 
                  className="btn-secondary"
                >
                  <Loader2 className="w-4 h-4 mr-2" />
                  Refresh Status
                </Button>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default AddCredits;
